import { Fragment, memo } from 'react';
import { Box, Thumbnail, Text } from '@nimbus-ds/components';
import { EditIcon, HistoryIcon } from '@nimbus-ds/icons';
import {
  ProductDetailsResponseDto,
  VariantResponseDto,
} from '@tiendanube/common';
import { DataList } from '@tiendanube/components';
import { LanguagesType } from 'App/i18n';
import { PopoverMenu } from 'commons/components';
import { useToast } from 'commons/hooks';
import { useHasPermission } from 'domains/Auth/hooks';
import { HiddenVariantTag } from 'domains/Catalog/Categories/components';
import {
  useInventory,
  useVariantsRange,
} from 'domains/Catalog/Inventory/pages/hooks';
import { joinVariantValues } from 'domains/Catalog/Inventory/utils';
import { HiddenProductPill } from 'domains/Catalog/Products/pages/ProductListPage/components';
import { LinkGoToProduct } from 'domains/Catalog/Products/pages/ProductListPage/components/ProductResultsDesktop/components/ResultRowDesktop/components';
import { stockForLocation } from 'domains/Catalog/Products/utils';
import useTranslationCatalog from 'domains/Catalog/useTranslationCatalog';
import useInventoryProductActions from '../../../hooks/useInventoryProductActions';
import { InputAsyncStock } from '../../InventoryResultsDesktop/components/ResultRowDesktop/components';
import { ShowHideVariantsLink } from '../../ShowHideVariantsLink';

const maxEntries = 10;

interface ProductItemListInterface {
  product: ProductDetailsResponseDto;
  language: LanguagesType;
  locationId?: string;
}

function InventoryItemList({
  product,
  language,
  locationId,
}: Readonly<ProductItemListInterface>) {
  const t = useTranslationCatalog();
  const { editStock } = useInventory();
  const { addToast } = useToast();
  const range = useVariantsRange(product, maxEntries);
  const {
    showAccessToStockHistory,
    handleOpenStockHistoryModal,
    handleOpenEditStockModal,
  } = useInventoryProductActions(product.id, locationId);

  const canEditStock = useHasPermission('edit_stock');

  if (!product) return null;

  const productName = product.name[language] || '';
  const thumbnail = {
    src: product.images[0]?.src ?? undefined,
    alt: product.images[0]?.alt?.[language] ?? productName,
  };
  const variants = range.variants;

  const handleEditStock =
    (id: string, locationId?: string) =>
    async ({ value }) => {
      await editStock({
        productId: product.id,
        action: 'replace',
        value: value !== '' ? Number(value) : null,
        variantId: id,
        locationId,
      });
    };

  const onErrorStock = () => {
    addToast({
      appearance: 'danger',
      label: t('products.toast.errorUpdateStock'),
    });
  };

  const actions = (variant: VariantResponseDto) => [
    ...(canEditStock
      ? [
          {
            label: t('inventory.actions.editStock'),
            startIcon: EditIcon,
            onClick: () => handleOpenEditStockModal(variant),
          },
        ]
      : []),
    ...(showAccessToStockHistory
      ? [
          {
            label: t('inventory.actions.viewStockHistory'),
            startIcon: HistoryIcon,
            onClick: () => handleOpenStockHistoryModal(variant),
          },
        ]
      : []),
  ];

  return (
    <DataList.Row id={product.id}>
      <Box
        display="grid"
        gridTemplateColumns="48px auto"
        gridGap="4"
        alignItems="flex-start"
        px="2"
        width="100%"
      >
        <Thumbnail {...thumbnail} />
        <Box display="flex" flexDirection="column" gap="2">
          <Box
            display="flex"
            flexDirection="row"
            gap="2"
            justifyContent="space-between"
          >
            <LinkGoToProduct title={productName} id={product.id} bold />
            {!product.published && <HiddenProductPill id={`${product.id}`} />}
          </Box>
          <Box
            display="grid"
            gridTemplateColumns="80px auto 32px"
            gridGap="2"
            alignItems="center"
            marginTop="2"
          >
            {variants.map((variant) => (
              <Fragment key={variant.id}>
                <InputAsyncStock
                  asyncFunc={handleEditStock(variant.id, locationId)}
                  defaultValue={stockForLocation(variant, locationId)}
                  onError={onErrorStock}
                  disabled={!canEditStock}
                />
                <Box display="flex" gap="2">
                  <Text lineClamp={1}>
                    {joinVariantValues(variant, language)}
                  </Text>
                  <HiddenVariantTag visible={variant.visible ?? true} />
                </Box>
                <PopoverMenu actions={actions(variant)} />
              </Fragment>
            ))}
          </Box>
          <ShowHideVariantsLink {...range} />
        </Box>
      </Box>
    </DataList.Row>
  );
}

export default memo(InventoryItemList);
